import { SignInIdentifier } from '@logto/schemas';
import { forwardRef, useId, useMemo, useState } from 'react';
import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import SmartInputField from '@/shared/components/InputFields/SmartInputField';
import type {
  IdentifierInputType,
  IdentifierInputValue,
} from '@/shared/components/InputFields/SmartInputField';

import styles from './index.module.scss';

type Option = 'student' | 'registration';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly isDanger?: boolean;
  readonly errorMessage?: string;
  readonly defaultValue?: string;
  readonly value?: IdentifierInputValue;
  readonly name?: string;
  readonly onBlur?: () => void;
  readonly onChange?: (data: IdentifierInputValue) => void;
};

/**
 * A student-oriented identifier field. Renders a radio group letting the user
 * declare whether they are entering a Student Number or a Registration Number,
 * then locks the underlying {@link SmartInputField} to username mode so numeric
 * values are never auto-detected as phone numbers. Both options submit as a
 * `username` identifier; the radio only changes the field label.
 */
const enabledTypes: IdentifierInputType[] = [SignInIdentifier.Username];

const StudentIdentifierField = (
  { className, onChange, ...rest }: Props,
  ref: Ref<HTMLInputElement>
) => {
  const { t } = useTranslation();
  const groupName = useId();
  const [option, setOption] = useState<Option>('student');

  const labelOverride = useMemo(
    () => t(option === 'student' ? 'input.student_number' : 'input.registration_number'),
    [option, t]
  );

  return (
    <div className={className}>
      <fieldset className={styles.field}>
        <legend className={styles.legend}>{t('input.student_identifier_legend')}</legend>
        <div className={styles.options}>
          <label className={styles.option}>
            <input
              type="radio"
              name={groupName}
              checked={option === 'student'}
              aria-label={t('input.student_number')}
              onChange={() => {
                setOption('student');
              }}
            />
            {t('input.student_number')}
          </label>
          <label className={styles.option}>
            <input
              type="radio"
              name={groupName}
              checked={option === 'registration'}
              aria-label={t('input.registration_number')}
              onChange={() => {
                setOption('registration');
              }}
            />
            {t('input.registration_number')}
          </label>
        </div>
      </fieldset>
      <SmartInputField
        {...rest}
        ref={ref}
        enabledTypes={enabledTypes}
        lockedType={SignInIdentifier.Username}
        labelOverride={labelOverride}
        onChange={onChange}
      />
    </div>
  );
};

export default forwardRef(StudentIdentifierField);
